// Picks a string out of the API's multilingual dicts (`{es, en, de, jp}`).
// The lookup rules are shared by web and mobile; each platform passes its own
// active language in, since core does not depend on i18next.

/** The API keys Japanese as "jp"; the frontend locale code for it is "ja". */
const API_LANGUAGE_ALIASES = { ja: 'jp' };

/** Last resort when the active locale has no entry in the dict. */
export const API_FALLBACK_LANGUAGE = 'en';

/**
 * Pick the entry for a language out of a backend multilingual dict.
 *
 * Tries the locale as given, then its base language with the `ja` → `jp` alias
 * applied, then English. A value is only accepted when it is a non-empty
 * string, so a blank or malformed entry falls through to the next candidate
 * instead of rendering an empty row.
 *
 * @param {Object<string, string>|null|undefined} textByLanguage Text keyed by
 *   language code, e.g. `{ es, en, de, jp }`.
 * @param {string|null|undefined} language The active locale, e.g. `de-DE`.
 * @returns {string|null} The localized string, or null when nothing usable is
 *   available. Callers must treat null as "render nothing at all".
 */
export function pickLocalizedText(textByLanguage, language) {
  if (!textByLanguage || typeof textByLanguage !== 'object') {
    return null;
  }

  const locale = String(language || '').toLowerCase();
  const base = locale.split('-')[0];
  const candidates = [
    locale,
    API_LANGUAGE_ALIASES[base] ?? base,
    API_FALLBACK_LANGUAGE,
  ];

  for (const candidate of candidates) {
    const value = textByLanguage[candidate];
    if (typeof value === 'string' && value.length > 0) {
      return value;
    }
  }

  return null;
}
